<script setup lang="ts">
import type { Product, Category, ProductLine } from '~/types'

const { fetchProducts } = useProducts()
const { fetchCategories, fetchProductLines, fetchComponentTypes } = useCategories()
const { filters } = useFilters()

// Pagination state
const page = ref(1)
const pageSize = 24

// Use useAsyncData for SSR-optimized initial load
const { data: initialData, pending: loading, refresh: refreshProducts } = await useAsyncData(
  'homepage-products',
  async () => {
    const [productsResult, cats, lines, types] = await Promise.all([
      fetchProducts(filters, page.value, pageSize),
      fetchCategories(),
      fetchProductLines(),
      fetchComponentTypes()
    ])
    return {
      products: productsResult.products,
      total: productsResult.total,
      categories: cats,
      productLines: lines,
      componentTypes: types
    }
  },
  {
    watch: [page], // Re-fetch when page changes
    default: () => ({
      products: [] as Product[],
      total: 0,
      categories: [] as Category[],
      productLines: [] as ProductLine[],
      componentTypes: [] as string[]
    })
  }
)

// Computed values from initial data
const products = computed(() => initialData.value?.products || [])
const total = computed(() => initialData.value?.total || 0)
const categories = computed(() => initialData.value?.categories || [])
const productLines = computed(() => initialData.value?.productLines || [])
const componentTypes = computed(() => initialData.value?.componentTypes || [])

// Handle filter changes
const handleFiltersChange = () => {
  page.value = 1
  refreshProducts()
}

// Handle search
const handleSearch = () => {
  page.value = 1
  refreshProducts()
}

// Total pages
const totalPages = computed(() => Math.ceil(total.value / pageSize))

// Template ref for catalog section (UContainer component ref)
const catalogSectionRef = ref<{ $el?: HTMLElement } | null>(null)

// Scroll to catalog
const scrollToCatalog = () => {
  if (import.meta.client && catalogSectionRef.value?.$el) {
    catalogSectionRef.value.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// Watch for filter changes - trigger refresh
watch(
  () => [
    filters.search,
    filters.categoryId,
    filters.productLineId,
    filters.componentType,
    filters.minPackSize,
    filters.maxPackSize
  ],
  () => {
    page.value = 1
    refreshProducts()
  }
)

// Scroll to catalog on page change (client-side only)
watch(page, (newPage, oldPage) => {
  if (newPage !== oldPage && import.meta.client && catalogSectionRef.value?.$el) {
    catalogSectionRef.value.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
})

// Enhanced SEO with Open Graph and Twitter cards
useSeoMeta({
  title: 'Catálogo de Productos - Granados Pinturas Industriales',
  description: 'Explore nuestro catálogo de pinturas industriales. Primarios, epóxicos, poliuretanos y más. Solicite su cotización.',
  ogTitle: 'Catálogo de Productos - Granados Pinturas Industriales',
  ogDescription: 'Explore nuestro catálogo de pinturas industriales. Primarios, epóxicos, poliuretanos y más.',
  ogType: 'website',
  ogUrl: 'https://granadospinturas.com',
  twitterCard: 'summary_large_image',
  twitterTitle: 'Catálogo de Productos - Granados Pinturas Industriales',
  twitterDescription: 'Explore nuestro catálogo de pinturas industriales'
})

// Canonical URL
useHead({
  link: [
    { rel: 'canonical', href: 'https://granadospinturas.com' }
  ]
})
</script>

<template>
  <div>
    <!-- Enhanced Hero Section -->
    <section class="relative bg-gradient-to-br from-red-600 via-red-500 to-red-700 text-white overflow-hidden">
      <!-- Decorative background pattern -->
      <div class="absolute inset-0 opacity-10">
        <div class="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
        <div class="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
      </div>

      <UContainer class="relative z-10">
        <div class="py-12 md:py-20 lg:py-24">
          <!-- Trust Badge -->
          <div class="flex items-center justify-center md:justify-start mb-6 md:mb-8">
            <div class="inline-flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 shadow-lg">
              <UIcon name="i-heroicons-shield-check-solid" class="text-2xl" />
              <span class="text-base md:text-lg font-semibold high-contrast">Distribuidor Autorizado de Pinturas Axalta</span>
            </div>
          </div>

          <!-- Main Heading -->
          <div class="text-center md:text-left max-w-4xl mx-auto md:mx-0 mb-8 md:mb-10">
            <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 high-contrast leading-tight">
              Pintura Automotriz y pintura decorativa
            </h1>
            <p class="text-xl md:text-2xl text-red-50 mb-6 leading-relaxed max-w-3xl">
              Soluciones profesionales para la industria de la construcción. Primarios, epóxicos, poliuretanos y más.
            </p>

            <!-- Key Benefits -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-8">
              <div class="flex items-center gap-3 justify-center md:justify-start">
                <UIcon name="i-heroicons-truck-solid" class="text-3xl md:text-4xl flex-shrink-0" />
                <span class="text-base md:text-lg font-medium">Envío a obra</span>
              </div>
              <div class="flex items-center gap-3 justify-center md:justify-start">
                <UIcon name="i-heroicons-clock-solid" class="text-3xl md:text-4xl flex-shrink-0" />
                <span class="text-base md:text-lg font-medium">Entrega rápida</span>
              </div>
              <div class="flex items-center gap-3 justify-center md:justify-start">
                <UIcon name="i-heroicons-chat-bubble-left-right-solid" class="text-3xl md:text-4xl flex-shrink-0" />
                <span class="text-base md:text-lg font-medium">Asesoría técnica</span>
              </div>
            </div>
          </div>

          <!-- Enhanced Search Section -->
          <div class="max-w-3xl mx-auto md:mx-0 mb-6">
            <div class="bg-white rounded-2xl shadow-2xl p-6 md:p-8">
              <!-- Search Label -->
              <label class="block text-gray-900 text-lg md:text-xl font-bold mb-4">
                Busca tu producto
              </label>

              <!-- Search Input -->
              <div class="mb-6">
                <CatalogProductSearch @search="handleSearch" />
              </div>

              <!-- Quick Actions -->
              <div class="flex flex-col sm:flex-row gap-4">
                <UButton
                  color="primary"
                  size="xl"
                  class="touch-target flex-1 text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-shadow"
                  icon="i-heroicons-rectangle-stack"
                  @click="scrollToCatalog"
                >
                  Ver Catálogo Completo
                </UButton>
                <UButton
                  color="neutral"
                  variant="solid"
                  size="xl"
                  class="touch-target flex-1 text-base md:text-lg font-semibold"
                  icon="i-heroicons-document-text"
                  to="/cotizacion"
                >
                  Solicitar Cotización
                </UButton>
              </div>
            </div>
          </div>

          <!-- Popular Categories - Quick Access -->
          <div class="hidden md:block max-w-3xl mx-auto md:mx-0">
            <p class="text-red-50 text-sm font-medium mb-3">Categorías populares:</p>
            <div class="flex flex-wrap gap-3">
              <UButton
                v-for="category in ['Primarios', 'Epóxicos', 'Poliuretanos', 'Esmaltes']"
                :key="category"
                color="white"
                variant="soft"
                size="lg"
                class="touch-target"
                @click="() => {
                  // Navigate to category filter
                  scrollToCatalog()
                }"
              >
                {{ category }}
              </UButton>
            </div>
          </div>
        </div>
      </UContainer>
    </section>

    <!-- Main Content -->
    <UContainer ref="catalogSectionRef" class="py-8">
      <div class="flex flex-col lg:flex-row gap-8">
        <!-- Sidebar Filters (Desktop) -->
        <aside class="hidden lg:block w-64 shrink-0">
          <div class="sticky top-24">
            <CatalogProductFilters
              :categories="categories"
              :product-lines="productLines"
              :component-types="componentTypes"
              @filters-change="handleFiltersChange"
            />
          </div>
        </aside>

        <!-- Products Area -->
        <div class="flex-1">
          <!-- Results count & Mobile filter button -->
          <div class="flex items-center justify-between mb-6">
            <p class="text-gray-600 dark:text-gray-400">
              <span v-if="!loading">
                {{ total }} producto{{ total !== 1 ? 's' : '' }} encontrado{{ total !== 1 ? 's' : '' }}
              </span>
              <span v-else>Cargando...</span>
            </p>

            <!-- Mobile filters button -->
            <UButton
              variant="outline"
              class="lg:hidden"
              icon="i-heroicons-funnel"
            >
              Filtros
            </UButton>
          </div>

          <!-- Products Grid -->
          <CatalogProductGrid
            :products="products"
            :loading="loading"
          />

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="mt-8 flex justify-center">
            <UPagination
              v-model:page="page"
              :total="total"
              :items-per-page="pageSize"
            />
          </div>
        </div>
      </div>
    </UContainer>
  </div>
</template>
